/**
 * D-2 订单 —— 契约 §7 D-2 / trade 的 `OrderDtos.java`。
 *
 * ★ 契约硬约束：**只读 + 可取消，永不建单**。下单只走 Agent 的确认卡片。
 *   所以本模块只有 list / detail / cancel 三个动作，没有、也不许有 create。
 */
import { z } from 'zod'

/**
 * 我方公共状态机（trade.proto `OrderState`，去掉 `UNSPECIFIED`）。
 *
 * ⚠ 这是**我方**状态，不是供应商状态码。列表筛选 `?status=` 传的就是这些名字，
 *   认不出的后端会 400（刻意不静默当「不过滤」）。
 */
export const ORDER_STATES = [
  'ORDER_CREATED',
  'ORDER_PENDING_PAYMENT',
  'ORDER_PAID',
  'ORDER_FULFILLING',
  'ORDER_COMPLETED',
  'ORDER_CANCELLED',
  'ORDER_REFUNDING',
  'ORDER_REFUNDED',
  'ORDER_FAILED',
] as const
export type OrderState = (typeof ORDER_STATES)[number]

/**
 * 列表一行（`OrderSummaryView`）。刻意窄：不含 `domainPayload`、行程段、内部主键。
 *
 * ⚠ `state` 用 `z.string()` 而不是 enum：后端将来加一个状态，前端不该整页解析失败 ——
 *   展示层按已知名给标签、认不出就原样显示。
 * ⚠ `totalAmount` 是**元**（后端 BigDecimal，JSON 里是数字如 `1280.00`）。JS 会读成 number，
 *   够精确用于展示；**别拿它做任何金额加减**（那才有精度风险），要算金额去后端。
 */
export const orderSummarySchema = z.looseObject({
  orderNo: z.string().min(1),
  /**
   * 业务域。⚠⚠ **值是【大写】**：`FLIGHT` / `HOTEL` / `RIDE`。
   *
   * ⚠ ~~这里原写「flight / hotel / …」（小写）是错的~~ —— 2026-09-11 读后端代码核实：
   *   `TradeOrderService:169` 存的是 `draft.domain().name().replace("DOMAIN_", "")`，
   *   即 proto 枚举名去前缀，**全大写**；`OrderController.parseDomain` 读回来时也是
   *   `Domain.valueOf("DOMAIN_" + stored)`，小写会直接抛。
   *   ⚠ 这种错法最难发现：按小写比较**不报错，只是永远不命中** ——
   *   界面看起来「就是没标签」，没人会怀疑是大小写。所以取用一律走
   *   {@link normaliseOrderDomain}，别在别处直接 `=== 'ride'`。
   * ⚠ 仍用 `z.string()`：后端加域时前端不该整页解析失败。
   */
  domain: z.string().nullable().default(null),
  state: z.string().min(1),
  totalAmount: z.number().nullable().default(null),
  currency: z.string().nullable().default(null),
  /** ISO-8601 本地时间串（如 `2026-09-02T14:30:00`）。 */
  createdAt: z.string().nullable().default(null),
  /** ⚠ **支付并推单成功后才有值**；未支付时为 null 是**正常状态**，别据此判定异常。 */
  supplierOrderNo: z.string().nullable().default(null),
})
export type OrderSummary = z.infer<typeof orderSummarySchema>

/**
 * 订单详情（`OrderDetailView`）。比列表多的就是 `domainPayload`。
 *
 * ⚠⚠ `domainPayload` 是 **JSON 字符串**，不是对象 —— 前端要 `JSON.parse` 一次。
 *   服务端不解析、原样透传（公共层不解释其内容）。解析失败要降级，不能白屏。
 */
export const orderDetailSchema = z.looseObject({
  orderNo: z.string().min(1),
  state: z.string().min(1),
  totalAmount: z.number().nullable().default(null),
  currency: z.string().nullable().default(null),
  supplierOrderNo: z.string().nullable().default(null),
  createdAt: z.string().nullable().default(null),
  /** ⚠ JSON 字符串。可能为 null（域没落 payload）。 */
  domainPayload: z.string().nullable().default(null),
})
export type OrderDetail = z.infer<typeof orderDetailSchema>

/**
 * 取消结果（`CancelResultView`）。
 *
 * ⚠⚠ **`status` 是取消后的真实新状态，不一定是 `ORDER_CANCELLED`**：已付款的订单取消后落
 *   `ORDER_REFUNDING`（有钱要退，不能进终态）。前端**必须按它显示**，别写死「取消成功 = 已取消」，
 *   那会让用户以为钱已经退了。
 */
export const cancelResultSchema = z.looseObject({
  status: z.string().min(1),
})
export type CancelResult = z.infer<typeof cancelResultSchema>

/** 取消请求体。`reason` 可空，但给几个常用原因让用户点，事后客诉才复现得了。 */
export const cancelOrderRequestSchema = z.looseObject({
  reason: z.string().max(200).optional(),
})
export type CancelOrderRequest = z.infer<typeof cancelOrderRequestSchema>

/**
 * 已知业务域。⚠ 与后端 `cione/common/v1/common.proto` 的 `Domain` 去掉 `DOMAIN_` 前缀一致。
 *
 * ⚠ `GENERAL` / `TICKET` 在 proto 里是**坑位**（ADR-001 明令新代码禁用），
 *   `PLANNER` 的域目录今天只有一个空 `__init__.py`，都不会出现在订单里 —— 故不列。
 */
export const ORDER_DOMAINS = ['FLIGHT', 'HOTEL', 'RIDE'] as const
export type OrderDomain = (typeof ORDER_DOMAINS)[number]

/**
 * 域名归一。⚠ 大小写不敏感是**刻意的防御**，不是纵容：后端今天给的是大写，
 * 但这个字段跨了三个服务，比较错了的表现是「标签永远不出现」而不是报错 ——
 * 这种错没人会怀疑到大小写上。
 */
export function normaliseOrderDomain(domain: string | null | undefined): OrderDomain | null {
  const upper = (domain ?? '').trim().toUpperCase()
  return (ORDER_DOMAINS as readonly string[]).includes(upper) ? (upper as OrderDomain) : null
}

/**
 * 🔴 这个域是不是**供应商直接向用户收款**（`SettlementMode.SUPPLIER`）。
 *
 * 打车域是：腾讯出行在**行程结束后直接向用户收**，我方一分不经手。带来两个后果，
 * 界面上都得认：
 *
 * 1. ⚠⚠ **`totalAmount` 存的是【预估价】，不是应收。** `RideOrderPlugin` 的注释写死了
 *    这一条。按「应付金额」原样显示会让用户以为这是要付给我们的钱 ——
 *    而他到站后付给腾讯的是另一个数（打表/实际里程）。
 * 2. ⚠ **永远不给支付入口。** 不过这一条**不必在界面上判域** ——
 *    后端 `OrderStateMachine` 禁止本档订单进入任何支付类状态，
 *    只按 `ORDER_PENDING_PAYMENT` 开支付入口就已经由构造排除了它
 *    （见 `payments.ts` 的 `PAYABLE_ORDER_STATES`）。
 *    🔴 **别在前端抄一份「哪些域是供应商直收」去挡支付** —— 抄一份就意味着
 *    将来新增后付费域时有两个地方要改，而漏掉的那个不报错、直接重复收款。
 *    本函数**只用于金额语义的展示**。
 */
export function isSupplierSettledDomain(domain: string | null | undefined): boolean {
  return normaliseOrderDomain(domain) === 'RIDE'
}
